import { useEffect, useState } from 'react';

/** Whether the viewer asked for less motion. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Counts a headline number up from 0 to `target` with an ease-out, so KPIs arrive with
 * the charts. Only the progress (0→1) is state, set from animation frames; the number is
 * derived from it. Restarts when `target` changes; shows `target` at once under reduced
 * motion. The frame is cancelled on unmount or retarget, so no update lands late.
 */
export function useCountUp(target: number | null | undefined, durationMs = 900): number | null {
  const goal = target ?? null;
  const [progress, setProgress] = useState({ goal, k: 0 });

  useEffect(() => {
    if (goal === null || prefersReducedMotion()) return undefined;
    let frame = 0;
    const start = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - start) / durationMs);
      setProgress({ goal, k });
      if (k < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [goal, durationMs]);

  if (goal === null || prefersReducedMotion()) return goal;
  // A progress value from a previous target restarts from 0 for the new one.
  const k = progress.goal === goal ? progress.k : 0;
  return goal * (1 - (1 - k) ** 3);
}
